import { isNotDefined } from '@sgme/fp';
import { type ReactElement, useMemo } from 'react';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { ClientsWaySwitch } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/CLientsWaySwitch.tsx';
import { CutOffMarketField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/CutOffMarketField.tsx';
import { DeliveryDateField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/DeliveryDateField.tsx';
import { ExpiryDateField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/ExpiryDateField.tsx';
import { NotionalField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/NotionalField.tsx';
import { SolvingGroup } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/SolvingGroup';
import { SpotRefField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/SpotRefField.tsx';
import { StartDateField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/StartDateField.tsx';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils';

interface FlexiForwardMainFieldsProps {
  tileId: string;
}

export function FlexiForwardMainFields({ tileId }: FlexiForwardMainFieldsProps): ReactElement {
  const { currency, tradeCapture, notionalCurrencyIndex } = useFlexiForwardSelector((flexiForward) => {
    const { currency1, currency2 } = getCurrenciesFromCurrencyPair(flexiForward.properties.currencyPair);
    const { notionalCurrencyIndex } = flexiForward.properties;

    if (isNotDefined(currency1) || isNotDefined(currency2)) {
      return {
        currency: '',
        notionalCurrencyIndex,
        tradeCapture: flexiForward.tradeCapture,
      };
    }

    return {
      currency: notionalCurrencyIndex === 1 ? currency1 : currency2,
      notionalCurrencyIndex,
      tradeCapture: flexiForward.tradeCapture,
    };
  });

  const validation = useMemo(
    () => (tradeCapture.status === 'CHECKED' ? tradeCapture?.validation : undefined),
    [tradeCapture],
  );

  return (
    <div className="p-8px">
      {/* CLIENT'S WAY */}
      <ClientsWaySwitch tileId={tileId} currency={currency} notionalCurrencyIndex={notionalCurrencyIndex ?? 1} />

      {/* NOTIONAL */}
      <NotionalField tileId={tileId} validation={validation?.notional} />

      {/* START DATE */}
      <StartDateField tileId={tileId} validation={validation?.startDate} />

      {/* EXPIRY DATE */}
      <ExpiryDateField tileId={tileId} validation={validation?.expiryDate} />

      {/* DELIVERY DATE */}
      <DeliveryDateField tileId={tileId} validation={validation?.deliveryDate} />

      {/* CUT OFF MARKET */}
      <CutOffMarketField tileId={tileId} />

      {/* SPOT REF. */}
      <SpotRefField tileId={tileId} validation={validation?.spotRef} />

      {/* SOLVING TYPE */}
      <SolvingGroup tileId={tileId} />
    </div>
  );
}
